import { useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Link, useLocation, useMatches, useNavigate } from 'react-router';
import { Select } from '@linktivity/link-ui';
import { useStore } from '@link/agent/stores';
import agentAuth from '@link/agent/vendors/firebase';
import { EnvBadge, Locale, User } from '@link/share/components';
import { clearAutoLogoutStorage } from '@link/share/hooks';
import { linktivity } from '@link/share/icons';
import styles from './header.module.css';

const HeaderView: React.FC = () => {
  const { auth, agent } = useStore();
  const matches = useMatches();
  const navigate = useNavigate();
  const location = useLocation();
  const pendingAgentRef = useRef<string | null>(null);

  // Apply pending agent switch only after the URL has changed to the list
  // page, so the detail component is already unmounted and cannot fire requests
  // with the new agent ID.
  useEffect(() => {
    if (pendingAgentRef.current === null) return;
    const value = pendingAgentRef.current;
    pendingAgentRef.current = null;
    agent.setSelectedAgentIdAndOrgId(value);
  }, [location.pathname, agent]);

  const logout = useCallback(async () => {
    clearAutoLogoutStorage();
    await agentAuth.logout();
    agent.clearSelectedAgentInfo();
  }, [agent]);

  const showSelect = () => {
    return !matches.some(
      match =>
        (match.handle as { hideAgentSelect?: boolean })?.hideAgentSelect ===
        true
    );
  };

  const handleAgentSelect = useCallback(
    (value: string) => {
      const detailParents = [
        '/book/search-activities',
        '/book/booking',
        '/bulk-order/search-activities',
        '/bulk-order/list',
        '/finance/transactions'
      ];
      for (const parent of detailParents) {
        if (location.pathname.startsWith(parent + '/')) {
          pendingAgentRef.current = value;
          navigate(parent, { replace: true });
          return;
        }
      }
      agent.setSelectedAgentIdAndOrgId(value);
    },
    [location.pathname, navigate, agent]
  );

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <div className={styles.brand}>
          <Link to="/" className={styles.logo}>
            <img src={linktivity} alt="logo" width="160" />
          </Link>
          <EnvBadge />
        </div>
        <div className={styles.right}>
          {auth.isLinkAdmin && showSelect() && (
            <div className={styles.select}>
              <Select
                options={agent.otaOpts}
                filterable
                value={agent.selectedAgentId}
                onSelect={handleAgentSelect}
                className={styles.input}
              />
              {agent.hasSubOrg && (
                <Select
                  options={agent.otaOrgOpts}
                  filterable
                  value={agent.selectedAgentOrgId}
                  onSelect={(value: string) => {
                    agent.setSelectedAgentOrgId(value);
                  }}
                  className={styles.input}
                />
              )}
            </div>
          )}
          <Locale />
          <User email={auth.user?.email || ''} logout={logout} />
        </div>
      </div>
    </header>
  );
};
const Header = observer(HeaderView);
export default Header;
